import { CreditCard, ExternalLink } from "lucide-react";
import { HOUSE_BRAND, STATEMENT_DESCRIPTOR, brandByKey } from "@school-sales-ai/shared";
import { useMyBilling } from "@/hooks/useBilling";
import { useClientBranding } from "@/hooks/useClientBranding";
import { useHouseBrandNotice } from "@/hooks/useHouseBrandNotice";
import { formatMxn } from "@/lib/format-mxn";

const DATE_FORMAT = new Intl.DateTimeFormat("es-MX", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const STATUS_LABELS: Record<string, string> = {
  active: "Activa",
  trialing: "En prueba",
  past_due: "Pago pendiente",
  unpaid: "Sin pagar",
  canceled: "Cancelada",
  incomplete: "Incompleta",
  incomplete_expired: "Expirada",
  paused: "Pausada",
};

const GOOD = "bg-[oklch(0.94_0.04_155)] text-[oklch(0.30_0.10_155)]";
const BAD = "bg-[oklch(0.95_0.03_16)] text-[oklch(0.42_0.16_16)]";
const NEUTRAL = "bg-ink-100 text-ink-500";

function tone(status: string | null): string {
  if (status === "active" || status === "trialing") return GOOD;
  if (status === "past_due" || status === "unpaid") return BAD;
  return NEUTRAL;
}

/**
 * A school's own view of its subscription.
 *
 * Deliberately thin — status, next charge, amount, and a way out to Stripe's
 * Customer Portal. Invoice history and card management are NOT rebuilt here: the
 * portal already does both, and the person who pays is usually not a CRM user at
 * all, so the portal's email magic link reaches them and this page would not.
 */
export function Facturacion(): React.JSX.Element {
  const { data, isPending, isError } = useMyBilling();
  // The school's own brand, not a literal: a Loxago shop subscribes to Loxago.
  const { data: branding } = useClientBranding();
  const brand = brandByKey(branding?.brand);
  // The charge is made by the house brand's Stripe account, under its descriptor.
  const noticeBrand = useHouseBrandNotice();

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-18 flex-shrink-0 items-center justify-between border-b border-ink-100 bg-surface px-6 py-4">
        <div>
          <h1 className="font-display text-[28px] font-extrabold leading-none text-ink-900">
            Facturación
          </h1>
          <p className="mt-0.5 text-xs text-ink-500">Tu suscripción a {brand.name}.</p>
          {noticeBrand && (
            <p className="mt-0.5 text-xs text-ink-500">
              El cargo aparece como {STATEMENT_DESCRIPTOR} en tu estado de cuenta:{" "}
              {HOUSE_BRAND.name} es la empresa detrás de {noticeBrand.name}.
            </p>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-auto p-6">
        {isError ? (
          <p className="text-sm text-[oklch(0.55_0.18_16)]">
            No se pudo cargar la información de facturación.
          </p>
        ) : isPending ? (
          <p className="text-sm text-ink-500">Cargando…</p>
        ) : !data.subscriptionStatus ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-ink-100">
              <CreditCard size={22} className="text-ink-400" />
            </div>
            <p className="font-display text-lg font-bold text-ink-700">
              Aún no hay una suscripción activa
            </p>
            <p className="max-w-sm text-center text-xs text-ink-500">
              Si crees que esto es un error, escríbenos y lo revisamos.
            </p>
          </div>
        ) : (
          <div className="max-w-lg rounded-lg border border-ink-100 bg-surface p-6 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                  Estado
                </p>
                <span
                  className={`mt-1.5 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${tone(data.subscriptionStatus)}`}
                >
                  {STATUS_LABELS[data.subscriptionStatus] ?? data.subscriptionStatus}
                </span>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                  Mensualidad
                </p>
                <p className="mt-1 font-display text-2xl font-bold text-ink-900">
                  {formatMxn(data.currentPriceAmountCents, data.currentPriceCurrency)}
                </p>
                {/* Which label is right depends on how this school was quoted. On
                    "+ IVA" the card is charged 16% more than the figure above, and
                    saying so here is what prevents the support call. */}
                {data.currentPriceAmountCents !== null && (
                  <p className="text-[11px] text-ink-500">
                    {data.taxBehavior === "inclusive" ? "IVA incluido" : "+ IVA"}
                  </p>
                )}
              </div>
            </div>

            {data.currentPeriodEnd && (
              <div className="mt-5 border-t border-ink-100 pt-4">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-500">
                  Próximo cobro
                </p>
                <p className="mt-1 text-sm text-ink-900">
                  {DATE_FORMAT.format(new Date(data.currentPeriodEnd))}
                </p>
              </div>
            )}

            {data.subscriptionStatus === "past_due" && (
              <div className="mt-5 rounded-lg bg-[oklch(0.97_0.02_16)] p-4">
                <p className="text-sm font-medium text-[oklch(0.42_0.16_16)]">
                  No pudimos procesar el último cobro
                </p>
                <p className="mt-1 text-xs text-ink-700">
                  Tu servicio sigue funcionando con normalidad. Actualiza tu método de
                  pago para evitar interrupciones.
                </p>
              </div>
            )}

            {data.portalUrl && (
              <a
                href={data.portalUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
              >
                Ver mis facturas <ExternalLink size={14} />
              </a>
            )}
            {data.portalUrl && (
              <p className="mt-2 text-xs text-ink-500">
                Se abre el portal de Stripe: historial de pagos, facturas y método de pago.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
